import React, { useContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import MaskedInput from 'react-text-mask';
import TextField from '@material-ui/core/TextField';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { StateContext } from '../../../state/index';
import { useCustomCSS } from '../../../utils/hooks';
import Title from '../../library/Title';
import ConfigComponent from '../../ConfigComponent';
import { ButtonContainer, Button } from '../../library/Buttons';
import StartOver from '../../library/StartOver';
import styles from './styles.module.scss';

function PhoneNumberMask(props) {
  const { inputRef, ...other } = props;

  return (
    <MaskedInput
      {...other}
      ref={(ref) => {
        inputRef(ref ? ref.inputElement : null);
      }}
      mask={[
        '(',
        /[1-9]/,
        /\d/,
        /\d/,
        ')',
        ' ',
        /\d/,
        /\d/,
        /\d/,
        '-',
        /\d/,
        /\d/,
        /\d/,
        /\d/,
      ]}
      placeholderChar={'\u2000'}
      showMask
      guide={false}
      autoFocus
    />
  );
}

PhoneNumberMask.propTypes = {
  inputRef: PropTypes.func.isRequired,
};

export const TwoFactorSimplifiedView = ({ onSubmit, isFetching }) => {
  const [
    {
      configuration: { recipientLogin },
    },
  ] = useContext(StateContext);
  const { twoFactor } = recipientLogin;
  const componentRef = useRef();

  useCustomCSS(componentRef, 'recipientLogin');

  return (
    <>
      <Title>
        <h1>
          <ConfigComponent config={recipientLogin.title} />
        </h1>
      </Title>
      <TwoFactor {...twoFactor} onSubmit={onSubmit} isFetching={isFetching} />
    </>
  );
};

const GetMobileNumber = ({
  description,
  value,
  onChange,
  isFetching,
  disabled,
  onSubmit,
  secondaryText,
  confirmId,
}) => {
  const [checked, setChecked] = useState(false);

  const toggleChecked = () => setChecked(!checked);

  return (
    <>
      <div className="description">
        <div>
          <ConfigComponent config={description} />
        </div>
      </div>
      <MobileNumberInput
        value={value}
        onChange={(evt) => {
          setChecked(false);
          onChange(evt);
        }}
      />
      {secondaryText && (
        <div className="description">
          <ConfigComponent config={secondaryText} />
        </div>
      )}
      {confirmId && (
        <div className={styles.twofactorIdConfirmation}>
          <div className={styles.confirmationDescription}>
            <ConfigComponent config={confirmId.description} />
          </div>
          <div className={styles.confirmationTextContainer}>
            <div className={styles.confirmationTextSpacing}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checked}
                    disabled={disabled}
                    onChange={toggleChecked}
                    name="checkedB"
                    color="primary"
                    inputProps={{ 'aria-label': 'secondary checkbox' }}
                  />
                }
                label={
                  <div className={styles.confirmationTextLabel}>
                    <ConfigComponent config={confirmId.confirmationText} />
                  </div>
                }
              />
            </div>
          </div>
        </div>
      )}

      <ButtonContainer>
        <Button
          label="Submit"
          loading={isFetching}
          disabled={disabled || !checked}
          onClick={onSubmit}
        />
      </ButtonContainer>
    </>
  );
};

const MobileNumberInput = ({ value, onChange }) => {
  return (
    <div style={{ margin: '10px 0 20px 0' }}>
      <FormControl>
        <TextField
          type="tel"
          value={value}
          onChange={onChange}
          variant="outlined"
          label="Phone Number"
          InputProps={{
            inputComponent: PhoneNumberMask,
          }}
          InputLabelProps={{
            shrink: true,
          }}
        />
      </FormControl>
    </div>
  );
};

const EmailAddressInput = ({ value, onChange, error, helperText }) => {
  return (
    <div style={{ margin: '10px 0 20px 0' }}>
      <FormControl>
        <TextField
          autoFocus
          type="email"
          style={{ width: 300 }}
          value={value}
          onChange={onChange}
          variant="outlined"
          label="Email Address"
          error={error}
          helperText={helperText}
          InputLabelProps={{
            shrink: true,
          }}
          FormHelperTextProps={{
            style: { margin: 0, padding: 0 },
          }}
        />
      </FormControl>
    </div>
  );
};

const GetEmailAddress = ({
  description,
  value,
  onChange,
  isFetching,
  disabled,
  onSubmit,
  secondaryText,
  confirmId,
}) => {
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState(false);
  const [helperText, setHelperText] = useState('');

  const toggleChecked = () => setChecked(!checked);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleEmailChange = (evt) => {
    const email = evt.target.value;
    setChecked(false);

    if (email && !validateEmail(email)) {
      setError(true);
      setHelperText('Please enter a valid email address');
    } else {
      setError(false);
      setHelperText('');
    }

    onChange(evt);
  };

  return (
    <>
      <div className="description">
        <div>
          <ConfigComponent config={description} />
        </div>
      </div>
      <EmailAddressInput
        value={value}
        onChange={handleEmailChange}
        error={error}
        helperText={helperText}
      />
      {secondaryText && (
        <div className="description">
          <ConfigComponent config={secondaryText} />
        </div>
      )}
      {confirmId && (
        <div className={styles.twofactorIdConfirmation}>
          <div className={styles.confirmationDescription}>
            <ConfigComponent config={confirmId.description} />
          </div>
          <div className={styles.confirmationTextContainer}>
            <div className={styles.confirmationTextSpacing}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checked}
                    disabled={disabled || error}
                    onChange={toggleChecked}
                    name="checkedB"
                    color="primary"
                    inputProps={{ 'aria-label': 'secondary checkbox' }}
                  />
                }
                label={
                  <div
                    className={styles.confirmationTextLabel}
                    style={{ maxWidth: 291 }}
                  >
                    <ConfigComponent config={confirmId.confirmationText} />
                  </div>
                }
              />
            </div>
          </div>
        </div>
      )}

      <ButtonContainer>
        <Button
          label="Submit"
          loading={isFetching}
          disabled={disabled || !checked || error}
          onClick={onSubmit}
        />
      </ButtonContainer>
    </>
  );
};

const TwoFactor = ({
  getId,
  confirmId,
  type = 'phone_number',
  onSubmit,
  isFetching,
}) => {
  const [id, setId] = useState('');

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const isPhoneNumber = type === 'phone_number';
  const isEmail = type === 'email_address';
  const isValid = isPhoneNumber ? id.length >= 14 : validateEmail(id);

  return (
    <>
      <section className="main">
        {isPhoneNumber && (
          <GetMobileNumber
            key="getMobileNumber"
            description={getId.description}
            secondaryText={getId.secondaryText}
            value={id}
            onChange={(evt) => setId(evt.target.value)}
            isFetching={isFetching}
            disabled={!isValid}
            onSubmit={() =>
              onSubmit({ [type]: id.replace(/[(|)]/g, '').replace(/\s/g, '-') })
            }
            confirmId={confirmId}
          />
        )}
        {isEmail && (
          <GetEmailAddress
            key="getEmailAddress"
            description={getId.description}
            secondaryText={getId.secondaryText}
            value={id}
            onChange={(evt) => setId(evt.target.value)}
            isFetching={isFetching}
            disabled={!isValid}
            onSubmit={() => onSubmit({ [type]: id })}
            confirmId={confirmId}
          />
        )}
        <StartOver
          style={{ padding: '0 0 1em 0', fontSize: 16, fontWeight: 'normal' }}
        />
      </section>
    </>
  );
};

export default TwoFactorSimplifiedView;
